import React, { useCallback, useRef, useState } from "react";
import { Icon, ButtonMenu, IconHighlight, Tooltip, Typography, CircularProgress } from "@ann/web-components";
import { useHomeChat } from "../../context";
import Editor from "../richText/RichText";
import "../richText/styles.css";
import { generatePdf, generateDocx } from "../../../../../components/DocumentCanvas/DocumentUtils";
import { useToast } from "../../../../../components/toast";
import SaveToDriveModal from "../SaveToDriveModal/SaveToDriveModal";
import { useDeleteDocument } from "../../hooks/useDeleteDocument";
import { useSaveDocumentToDrive } from "../../hooks/useSaveDocumentToDrive/useSaveDocumentToDrive";
import { useDocumentRewrite } from "../../hooks/useDocumentRewrite/useDocumentRewrite";
import {
  Panel,
  Header,
  Title,
  TitleInput,
  TitleTooltipTrigger,
  TitleClickable,
  TitleEditWrapper,
  Content,
  EditorWrapper,
  HeaderActions,
  HeaderButton,
  HeaderMenuWrapper,
  LoadingBlurBackdrop,
} from "./DocumentPanel.styled";
import DeleteDocumentModal from "./DeleteDocumentModal";
import { DocumentTagsSection } from "./components/DocumentTagsSection";

/**
 * onBack: optional — shows a back arrow that returns to the conversation (used in ChatSlideOut).
 * onCloseAll: optional — overrides the × button to close the entire parent (used in ChatSlideOut).
 * Without these props the component behaves exactly as before (HomePageV2 is unaffected).
 */
const DocumentPanel = ({ onBack, onCloseAll }) => {
  const {
    isDocumentPanelOpen,
    documentPanelTitle,
    documentPanelContent,
    closeDocumentPanel,
    currentDocumentId,
    currentDocument,
    updateDocument,
    selectedAgentId,
    userId,
    tenantId,
  } = useHomeChat();
  const toast = useToast();
  const { handleDeleteDocument } = useDeleteDocument();
  const lastEditorStateRef = useRef(null);
  const undoRef = useRef(null);
  const titleInputRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSaveToVaultModal, setShowSaveToVaultModal] = useState(false);
  const [editorResetKey, setEditorResetKey] = useState(0);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const handleContentChange = useCallback(
    (editorState) => {
      lastEditorStateRef.current = editorState;
      if (currentDocumentId && editorState?.toText) {
        const text = editorState.toText();
        updateDocument(currentDocumentId, { content: text });
      }
    },
    [currentDocumentId, updateDocument]
  );

  const { rewrite, isRewriting } = useDocumentRewrite({
    getContent: () =>
      lastEditorStateRef.current?.toText?.() ??
      currentDocument?.content ??
      "",
    onReplaceContent: (newContent) => {
      if (!currentDocumentId) return;
      updateDocument(currentDocumentId, { content: newContent });
      setEditorResetKey((prev) => prev + 1);
    },
    getUserId: () => userId,
  });

  const handleRegisterUndo = useCallback(({ undo }) => {
    undoRef.current = undo;
  }, []);

  const handleUndoClick = useCallback(() => {
    undoRef.current?.();
  }, []);

  const handleAiChatClick = () => {
    // De momento solo abre/cierra la sección de AI; la lógica pesada vive en rewrite
  };

  const handleAiApply = useCallback(
    async (prompt) => {
      try {
        await rewrite(prompt);
      } catch (error) {
        toast.error({
          message:
            error?.message ?? "Failed to apply AI improvements to document.",
        });
      }
    },
    [rewrite, toast]
  );

  const handleAiTagClick = useCallback(
    async (text) => {
      try {
        await rewrite(text);
      } catch (error) {
        toast.error({
          message:
            error?.message ?? "Failed to apply AI suggestion to document.",
        });
      }
    },
    [rewrite, toast]
  );

  const { saveToVault, isSaving } = useSaveDocumentToDrive({
    getContent: () =>
      lastEditorStateRef.current ??
      (currentDocument?.content != null
        ? { toText: () => currentDocument.content }
        : ""),
    getTitle: () => currentDocument?.title ?? documentPanelTitle ?? "Document",
    getAgentInfo: () => ({
      agentUuid: selectedAgentId,
    }),
    getAuthContext: () => ({
      userId,
      tenantId,
    }),
    onSuccess: (message) => {
      toast.success({ message });
    },
    onError: (error) => {
      toast.error({ message: error?.message ?? "Failed to save document." });
    },
  });

  const handleDownloadChange = useCallback(
    (value) => {
      if (value !== "docx" && value !== "pdf") return;

      const editorState =
        lastEditorStateRef.current ??
        (currentDocument?.content != null
          ? { toText: () => currentDocument.content }
          : null);

      const rawTitle =
        currentDocument?.title ?? documentPanelTitle ?? "Document";
      const title = rawTitle.replace(/\s+/g, "_").trim() || "Document";

      const content =
        editorState?.toText?.() ?? currentDocument?.content ?? "";
      const isEmpty =
        !content?.trim() || content === "No content available";

      if (isEmpty) {
        toast.error({ message: "Document is empty." });
        return;
      }

      const stateToUse = editorState ?? { toText: () => content };

      setIsDownloading(true);
      const promise =
        value === "pdf"
          ? generatePdf(stateToUse, title)
          : generateDocx(stateToUse, title);
      promise
        .catch((err) => {
          toast.error({
            message: err?.message ?? `Failed to generate ${value.toUpperCase()}.`,
          });
        })
        .finally(() => setIsDownloading(false));
    },
    [currentDocument, documentPanelTitle, toast]
  );

  const handleSaveToDriveClick = useCallback(() => {
    setShowSaveToVaultModal(true);
  }, []);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleMenuChange = useCallback((value) => {
    if (value === "delete-document") setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!currentDocument) return;
    await handleDeleteDocument(currentDocument);
  }, [currentDocument, handleDeleteDocument]);

  const handleAddTag = useCallback(
    (newTag) => {
      if (!currentDocumentId) {
        return;
      }
      const currentTags = currentDocument?.tags || [];
      updateDocument(currentDocumentId, {
        tags: [...currentTags, newTag],
      });
    },
    [currentDocumentId, currentDocument, updateDocument]
  );

  const handleRemoveTag = useCallback(
    (tagToRemove) => {
      if (!currentDocumentId) return;
      const currentTags = currentDocument?.tags || [];
      updateDocument(currentDocumentId, {
        tags: currentTags.filter((t) => t !== tagToRemove),
      });
    },
    [currentDocumentId, currentDocument, updateDocument]
  );

  const handleTitleClick = useCallback(() => {
    setIsEditingTitle(true);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  }, []);

  const handleTitleChange = useCallback(
    (e) => {
      const newTitle = e.target.value;
      if (currentDocumentId) {
        updateDocument(currentDocumentId, {
          title: newTitle,
        });
      }
    },
    [currentDocumentId, updateDocument]
  );

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    if (currentDocumentId && currentDocument) {
      const currentTitle = currentDocument.title || documentPanelTitle || "";
      if (!currentTitle.trim()) {
        updateDocument(currentDocumentId, {
          title: "Untitled Document",
        });
      }
    }
  }, [currentDocumentId, currentDocument, documentPanelTitle, updateDocument]);

  if (!isDocumentPanelOpen) return null;

  const title = currentDocument?.title ?? documentPanelTitle;
  const initialText = currentDocument?.content ?? documentPanelContent ?? "";
  const tags = currentDocument?.tags || [];

  return (
    <Panel>
      <Header>
        <Title>
          {onBack && (
            <HeaderButton onClick={onBack} aria-label="Back to conversation">
              <Icon color="neutral-black" name="arrowLeft" size="medium" />
            </HeaderButton>
          )}
          <IconHighlight
            bgColor="LightBlue"
            icon="article"
            iconColor="Primary"
            size="small"
          />
          {isEditingTitle ? (
            <TitleEditWrapper>
              <TitleInput
                ref={titleInputRef}
                value={title}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleTitleBlur();
                  }
                }}
              />
            </TitleEditWrapper>
          ) : (
            <Tooltip content="Click to edit name" position="bottom" arrow={false} zIndex={99999999}>
              <TitleTooltipTrigger>
                <TitleClickable onClick={handleTitleClick}>
                  <Typography
                    category="title"
                    size="medium"
                    weight="bold"
                  >
                    {title}
                  </Typography>
                </TitleClickable>
              </TitleTooltipTrigger>
            </Tooltip>
          )}
        </Title>
        <HeaderActions>
          <Tooltip content="Undo action" position="bottom" arrow={false} zIndex={99999999}>
            <span style={{ display: "inline-flex" }}>
              <HeaderButton onClick={handleUndoClick} aria-label="Undo action">
                <Icon color="neutral" name="undo" size="medium" />
              </HeaderButton>
            </span>
          </Tooltip>

          <Tooltip content="Options" position="bottom" arrow={false} zIndex={99999999}>
            <HeaderMenuWrapper>
              <ButtonMenu
              alignMenu="left"
              customIcon="dotsMenu"
              disableEffects
              iconPosition="right"
              label=""
              menuPosition="bottom"
              onChange={handleMenuChange}
              size="medium"
              type="tertiary"
              usePortal
              width="auto"
              aria-label="Options"
            >
              <ButtonMenu.Menu icon={true}>
                <ButtonMenu.Item
                  label="Delete document"
                  value="delete-document"
                  icon="delete2"
                />
              </ButtonMenu.Menu>
            </ButtonMenu>
          </HeaderMenuWrapper>
          </Tooltip>

          <HeaderButton onClick={onCloseAll ?? closeDocumentPanel} aria-label="Close">
            <Icon color="neutral" name="close" size="medium" />
          </HeaderButton>
        </HeaderActions>
      </Header>
      <DocumentTagsSection
        tags={tags}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
      />
      <Content>
        <EditorWrapper>
          {isRewriting && (
            <LoadingBlurBackdrop aria-hidden="true">
              <CircularProgress size="xl" />
            </LoadingBlurBackdrop>
          )}
          <Editor
            key={`${currentDocumentId ?? "empty"}-${editorResetKey}`}
            initialText={initialText}
            onChange={handleContentChange}
            onRegisterUndo={handleRegisterUndo}
            onAiChatClick={handleAiChatClick}
            onAiApply={handleAiApply}
            onAiTagClick={handleAiTagClick}
            onDownloadChange={handleDownloadChange}
            onSaveToDriveClick={handleSaveToDriveClick}
            isDownloading={isDownloading}
            isSavingToDrive={isSaving}
          />
        </EditorWrapper>
      </Content>
      <SaveToDriveModal
        isOpen={showSaveToVaultModal}
        onClose={() => setShowSaveToVaultModal(false)}
        documentTitle={currentDocument?.title ?? documentPanelTitle ?? "Document"}
        onSave={async (fileName, fileType, destinations) => {
          const result = await saveToVault(fileName, fileType, destinations);
          if (result && currentDocumentId && updateDocument) {
            updateDocument(currentDocumentId, {
              driveUuid: result.vaultUuid,
              vaultName: result.vaultName,
              title: result.fileName ?? fileName,
            });
          }
          setShowSaveToVaultModal(false);
        }}
      />
      <DeleteDocumentModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        documentTitle={title}
      />
    </Panel>
  );
};

export default DocumentPanel;
